import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, X } from "lucide-react";
import { authHeader } from "@/lib/auth";
import { resolveImg, formatDate } from "@/lib/blog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", tags: "", published: true };

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null = list view
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/blogs`, { headers: authHeader() });
      setPosts(data);
    } catch { /* handled by parent 401 */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p, tags: (p.tags || []).join(", ") }); setEditing(p.id); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await axios.post(`${API}/blogs/upload`, fd, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Cover uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    const payload = {
      title: form.title, excerpt: form.excerpt, content: form.content,
      cover_image: form.cover_image || null,
      tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      published: !!form.published,
    };
    try {
      if (editing === "new") await axios.post(`${API}/blogs`, payload, { headers: authHeader() });
      else await axios.put(`${API}/blogs/${editing}`, payload, { headers: authHeader() });
      toast.success("Post saved.");
      cancel();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/blogs/${id}`, { headers: authHeader() });
      toast.success("Post deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const inputCls = "w-full bg-white border border-konattu-charcoal/20 rounded-none px-4 py-3 font-body text-konattu-charcoal focus:outline-none focus:border-konattu-red transition-colors";

  if (editing) {
    return (
      <form onSubmit={save} className="space-y-6" data-testid="blog-admin-form">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-2xl text-konattu-charcoal">{editing === "new" ? "New post" : "Edit post"}</h3>
          <button type="button" onClick={cancel} data-testid="blog-admin-cancel" className="inline-flex items-center gap-2 text-sm text-konattu-charcoal/60 hover:text-konattu-red"><X size={15} /> Cancel</button>
        </div>
        <input data-testid="blog-admin-title" className={inputCls} placeholder="Post title" value={form.title} onChange={set("title")} />
        <textarea data-testid="blog-admin-excerpt" rows={2} className={inputCls} placeholder="Short excerpt / summary" value={form.excerpt} onChange={set("excerpt")} />
        <textarea data-testid="blog-admin-content" rows={12} className={`${inputCls} font-mono text-sm`} placeholder="Content (Markdown supported)" value={form.content} onChange={set("content")} />
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="blog-admin-tags" className={inputCls} placeholder="Tags (comma separated)" value={form.tags} onChange={set("tags")} />
          <label className="flex items-center gap-3 border border-dashed border-konattu-charcoal/30 px-4 py-3 cursor-pointer hover:border-konattu-red transition-colors">
            <Upload size={16} className="text-konattu-red" />
            <span className="font-body text-sm text-konattu-charcoal/70 truncate">{uploading ? "Uploading..." : form.cover_image ? "Change cover image" : "Upload cover image"}</span>
            <input type="file" accept="image/*" onChange={uploadCover} className="hidden" data-testid="blog-admin-cover-input" />
          </label>
        </div>
        {form.cover_image && <img src={resolveImg(form.cover_image)} alt="cover" className="h-40 w-auto object-cover border border-konattu-charcoal/15" />}
        <label className="flex items-center gap-2 text-sm text-konattu-charcoal">
          <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="blog-admin-published" /> Published (visible on site)
        </label>
        <button type="submit" disabled={saving} data-testid="blog-admin-save" className="bg-konattu-red text-white px-8 py-3.5 text-sm font-medium tracking-wide hover:bg-konattu-orange transition-colors disabled:opacity-60">
          {saving ? "Saving..." : "Save post"}
        </button>
      </form>
    );
  }

  return (
    <div data-testid="blog-admin-list">
      <button onClick={openNew} data-testid="blog-admin-new" className="mb-6 inline-flex items-center gap-2 bg-konattu-red text-white px-5 py-2.5 text-sm hover:bg-konattu-orange transition-colors">
        <Plus size={16} /> New post
      </button>
      {posts.length === 0 ? (
        <div className="border border-konattu-charcoal/15 p-16 text-center"><p className="text-konattu-charcoal/60">No posts yet.</p></div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="bg-white border border-konattu-charcoal/10 p-5 flex items-center justify-between gap-4" data-testid="blog-admin-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-display font-semibold text-konattu-charcoal truncate">{p.title}</span>
                  {!p.published && <span className="bg-konattu-charcoal/10 text-konattu-charcoal/70 px-2 py-0.5 text-[10px] font-mono">DRAFT</span>}
                </div>
                <div className="font-mono text-[11px] text-konattu-charcoal/40 mt-1">/{p.slug} · {formatDate(p.created_at)}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => openEdit(p)} data-testid="blog-admin-edit" className="p-2 border border-konattu-charcoal/20 hover:border-konattu-red hover:text-konattu-red transition-colors"><Pencil size={15} /></button>
                <button onClick={() => remove(p.id)} data-testid="blog-admin-delete" className="p-2 border border-konattu-charcoal/20 hover:border-konattu-red hover:text-konattu-red transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
